import React, { useState, useRef } from 'react';
import ReCAPTCHA from "react-google-recaptcha";
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import { isValidEmail, getErrorMessage } from '../utils/helpers';

const SignupPage = () => {
  const navigate = useNavigate();
  const { signup, verifyOtp } = useAuth(); // Ensure verifyOtp is defined in AuthContext
  const recaptchaRef = useRef();

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const [otp, setOtp] = useState('');
  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateDetails = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!isValidEmail(formData.email)) newErrors.email = 'Invalid email address';
    if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Step 1: Handle Initial Signup (Triggers OTP Email)
  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!validateDetails()) return;


    const token = recaptchaRef.current.getValue();
    if (!token) {
      setErrors({ general: "Please complete the CAPTCHA" });
      return;
    }

    try {
      setLoading(true);
      // Calls Spring Boot @PostMapping("/signup")
      await signup({
        fullName: formData.fullName,
        email: formData.email,
        password: formData.password,
        captchaToken: token,
      });

      setShowOtpScreen(true);
      setErrors({});
    } catch (error) {
      if (error.response && error.response.status === 409) {
        setErrors({ general: "This email is already registered. Please login instead." });
      }
      else{
      setErrors({ general: getErrorMessage(error) });
      recaptchaRef.current.reset();}
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Handle OTP Verification (Finalizes Registration)
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp || otp.length < 6) {
      setErrors({ otp: "Please enter a valid 6-digit OTP" });
      return;
    }

    try {
      setLoading(true);
      // Option 2: Send OTP + Original DTO data back
      await verifyOtp({
        otp,
        signupData: {
          fullName: formData.fullName,
          email: formData.email,
          password: formData.password
        }
      });

      navigate('/');
    } catch (error) {
      setErrors({ general: "Invalid or expired OTP. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900">
            {showOtpScreen ? 'Verify Email' : 'Create Account'}
          </h2>
          <p className="mt-2 text-gray-600">
            {showOtpScreen ? `Code sent to ${formData.email}` : 'Join CollegeDrip today'}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-8">
          {errors.general && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
              {errors.general}
            </div>
          )}

          {!showOtpScreen ? (
            /* Registration Form */
            <form onSubmit={handleRequestOtp} className="space-y-6">
              <Input
                label="Full Name"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                error={errors.fullName}
                required
              />
              <Input
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={errors.email}
                required
              />
              <Input
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                error={errors.password}
                required
              />
              <Input
                label="Confirm Password"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                error={errors.confirmPassword}
                required
              />

              <div className="flex justify-center">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey="6Ld3YpssAAAAAICJnd0CLnm9iuGCzAeCJGONU8oC"
                  onChange={() => setErrors(prev => ({ ...prev, general: '' }))}
                />
              </div>

              <Button type="submit" fullWidth loading={loading} size="lg">
                Submit
              </Button>
            </form>
          ) : (
            /* OTP Verification Form */
            <form onSubmit={handleVerifyOtp} className="space-y-6">
              <Input
                label="Enter 6-Digit OTP"
                placeholder="000000"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                error={errors.otp}
                required
              />
              
              <Button type="submit" fullWidth loading={loading} size="lg">
                Verify & Register
              </Button>

              <button
                type="button"
                className="w-full text-sm text-blue-600 hover:text-blue-700"
                onClick={() => setShowOtpScreen(false)}
              >
                Change email or details
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-blue-600 hover:text-blue-700">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;